import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  BookOpen,
  History,
  LayoutTemplate,
  Radar,
  Server,
  ShieldCheck,
  SquareTerminal,
  Wrench,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PendingValue } from '@/components/surface/ui';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RUNS_KEY, automationApi } from '@/lib/automation-api';
import { BACKUPS_KEY, backupsApi } from '@/lib/backups-api';
import { getErrorMessage } from '@/lib/errors';
import { FLEET_METRICS_KEY, metricsApi } from '@/lib/metrics-api';
import { INCIDENTS_KEY, MONITORS_KEY, monitoringApi } from '@/lib/monitoring-api';
import { serversApi } from '@/lib/servers-api';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import { TrialPanel } from '@/modules/billing/TrialPanel';
import { AttentionPanel, type AttentionItem } from '@/modules/dashboard/AttentionPanel';
import { ServerFleet } from '@/modules/dashboard/ServerFleet';
import { relativeTime } from '@/modules/monitoring/format';
import {
  expiryLabel,
  formatUptimePercent,
  isExpiryKind,
  monitorUrgency,
  summarizeMonitors,
} from '@/modules/monitoring/summary';
import type { BackupRecord, Monitor, Run } from '@/types/api';

/** Rows the two lists at the foot of the page show before pointing at their own page. */
const LIST_ROWS = 6;

const failedRun = (run: Run) => run.status === 'failed' || run.status === 'partially_failed';

/**
 * The customer's overview.
 *
 * ## It leads with what is wrong, not with how much they have
 *
 * The count of servers is something the customer already knows; whether one of them stopped
 * answering twenty minutes ago, or a run is waiting for them to confirm its canary, is not. So the
 * attention panel is first and is the only section that grows.
 *
 * ## Then the figures, the fleet, and the two lists last
 *
 * Four counts, the servers themselves, and at the foot uptime and the latest backups side by side.
 * There is no list of recent runs: Automation → Runs is that list, with its filters, and a second
 * copy here only pushed the servers further down. A run that needs somebody still reaches this page
 * — waiting on a canary, or failed in the last day — as an attention item.
 *
 * ## Nothing here invents a number
 *
 * Every figure shows an em dash until its query has actually succeeded. A dashboard that renders `0`
 * while loading, or after a failed request, tells the customer they have no servers — and
 * "0 monitors down" is the specific lie this page must never tell.
 */
export function DashboardPage() {
  const options = { staleTime: 30_000 };

  const inventory = useQuery({ queryKey: ['server-inventory'], queryFn: () => serversApi.inventory(), ...options });
  const monitors = useQuery({ queryKey: MONITORS_KEY, queryFn: () => monitoringApi.list(), ...options });
  // Read for one line: what the platform fixed on its own this week.
  const incidents = useQuery({ queryKey: INCIDENTS_KEY, queryFn: () => monitoringApi.incidents(), ...options });
  const backups = useQuery({ queryKey: BACKUPS_KEY, queryFn: () => backupsApi.overview(), ...options });
  const waitingRuns = useQuery({
    queryKey: [...RUNS_KEY, 'dashboard', 'awaiting'],
    queryFn: () => automationApi.listRuns({ status: 'awaiting_confirmation', limit: 10 }),
    refetchInterval: 15_000,
  });
  // Not drawn as a list: counted for the runs tile, and read for failures in the last day.
  const recentRuns = useQuery({
    queryKey: [...RUNS_KEY, 'dashboard', 'recent'],
    queryFn: () => automationApi.listRuns({ limit: 25 }),
    refetchInterval: 15_000,
  });
  const fleet = useQuery({
    queryKey: FLEET_METRICS_KEY,
    queryFn: () => metricsApi.fleet(),
    // Matches the collector's five-minute interval.
    refetchInterval: 300_000,
    ...options,
  });

  const attention: AttentionItem[] = [];

  // ── Automation ─────────────────────────────────────────────────────────────
  if (waitingRuns.isSuccess) {
    const waiting = waitingRuns.data.items;

    if (waiting.length > 0) {
      attention.push({
        id: 'runs-awaiting',
        severity: 'warning',
        message:
          waiting.length === 1
            ? `“${waiting[0].title}” succeeded on its canary server and is waiting for you to continue.`
            : `${waiting.length} runs succeeded on their canary server and are waiting for you to continue.`,
        to: waiting.length === 1 ? `/automation/runs/${waiting[0].id}` : '/automation/runs?status=awaiting_confirmation',
        action: 'Review',
      });
    }
  }

  const dayAgo = Date.now() - 86400 * 1000;
  const runsToday = recentRuns.isSuccess
    ? recentRuns.data.items.filter((run) => new Date(run.created_at).getTime() >= dayAgo)
    : [];
  const failedToday = runsToday.filter(failedRun);
  const unfinishedToday = runsToday.filter((run) => !run.is_finished);

  if (failedToday.length > 0) {
    attention.push({
      id: 'runs-failed',
      severity: 'warning',
      message:
        failedToday.length === 1
          ? `“${failedToday[0].title}” did not succeed on every server: ${failedToday[0].summary}`
          : `${failedToday.length} runs in the last day did not succeed on every server.`,
      to: failedToday.length === 1 ? `/automation/runs/${failedToday[0].id}` : '/automation/runs',
      action: failedToday.length === 1 ? 'Open run' : 'Open runs',
    });
  }

  // ── Monitoring ─────────────────────────────────────────────────────────────
  if (monitors.isSuccess) {
    const down = monitors.data.monitors.filter((m) => m.status === 'down' && !m.is_stale);
    const stale = monitors.data.monitors.filter((m) => m.is_stale);

    if (down.length > 0) {
      attention.push({
        id: 'monitors-down',
        severity: 'critical',
        message:
          down.length === 1
            ? `${down[0].name} is down: ${down[0].last_detail}`
            : `${down.length} monitors are reporting their targets as down.`,
        to: '/monitoring',
        action: 'Open monitoring',
      });
    }

    // A stale monitor is not reporting a problem, it is failing to report at all. Folding the two
    // together would let a stopped scheduler hide behind green.
    if (stale.length > 0) {
      attention.push({
        id: 'monitors-stale',
        severity: 'warning',
        message:
          stale.length === 1
            ? `${stale[0].name} has not been checked recently, so its status may be out of date.`
            : `${stale.length} monitors have not been checked recently. Their status may be out of date.`,
        to: '/monitoring',
        action: 'Open monitoring',
      });
    }

    // Read off the TLS handshake, so this is what the server is actually serving.
    const expiring = monitors.data.monitors.filter(
      (m) => m.certificate_days_remaining !== null && m.certificate_days_remaining <= 14,
    );

    if (expiring.length > 0) {
      const soonest = Math.min(...expiring.map((m) => m.certificate_days_remaining ?? 0));
      attention.push({
        id: 'certificate-expiring-served',
        severity: soonest <= 3 ? 'critical' : 'warning',
        message:
          expiring.length === 1
            ? `${expiring[0].name} is serving a certificate that expires in ${soonest} day${soonest === 1 ? '' : 's'}.`
            : `${expiring.length} sites are serving certificates that expire within 14 days.`,
        to: '/monitoring',
        action: 'Open monitoring',
      });
    }
  }

  // ── Backups ────────────────────────────────────────────────────────────────
  if (backups.isSuccess) {
    const staleSchedules = backups.data.schedules.filter((s) => s.is_stale);
    const failures = backups.data.backups.filter((b) => b.status === 'failed');
    const scheduled = new Set(backups.data.schedules.map((s) => s.source_id));
    const unprotected = backups.data.sources.filter((source) => !scheduled.has(source.id));

    if (staleSchedules.length > 0) {
      attention.push({
        id: 'backups-stale',
        severity: 'critical',
        message:
          staleSchedules.length === 1
            ? `Backups of ${staleSchedules[0].source_name} are switched on but have not produced a copy recently.`
            : `${staleSchedules.length} backup schedules are switched on but are not producing copies.`,
        to: '/backups',
        action: 'Open backups',
      });
    }

    if (failures.length > 0) {
      attention.push({
        id: 'backups-failed',
        severity: 'warning',
        message:
          failures.length === 1
            ? `The last backup of ${failures[0].source_name} failed.`
            : `${failures.length} recent backups failed.`,
        to: '/backups',
        action: 'Open backups',
      });
    }

    // A copy that exists and would not have restored: the one the customer would otherwise discover
    // on the day they need it.
    const failedTests = backups.data.verification.restore_tests.filter((t) => t.status === 'failed');

    if (failedTests.length > 0) {
      attention.push({
        id: 'backups-restore-failed',
        severity: 'critical',
        message:
          failedTests.length === 1
            ? `The latest restore test of ${failedTests[0].source_name} failed: that backup would not have restored.`
            : `${failedTests.length} sources failed their latest restore test.`,
        to: '/backups',
        action: 'Open backups',
      });
    } else if (backups.data.verification.summary.backups_failed_verification > 0) {
      const broken = backups.data.verification.summary.backups_failed_verification;
      attention.push({
        id: 'backups-verification-failed',
        severity: 'warning',
        message:
          broken === 1
            ? 'A kept backup failed a check and no longer counts towards the copies you keep.'
            : `${broken} kept backups failed a check and no longer count towards the copies you keep.`,
        to: '/backups',
        action: 'Open backups',
      });
    }

    // Only once there is somewhere to put backups: nagging before a destination exists tells them
    // off for not finishing a setup they have not started.
    if (unprotected.length > 0 && backups.data.destinations.length > 0) {
      attention.push({
        id: 'backups-missing',
        severity: 'warning',
        message:
          unprotected.length === 1
            ? `${unprotected[0].name} has no backup schedule.`
            : `${unprotected.length} backup sources have no schedule.`,
        to: '/backups',
        action: 'Set one up',
      });
    }
  }

  // ── Servers ────────────────────────────────────────────────────────────────
  if (inventory.isSuccess) {
    const unreachable = (inventory.data.counts.by_status.offline ?? 0) + (inventory.data.counts.by_status.unreachable ?? 0);

    if (unreachable > 0) {
      attention.push({
        id: 'servers-unreachable',
        severity: 'critical',
        message:
          unreachable === 1
            ? 'A server failed its last connection check. Nothing can run on it until it answers.'
            : `${unreachable} servers failed their last connection check. Nothing can run on them until they answer.`,
        to: '/servers',
        action: 'Open servers',
      });
    }
  }

  // ── Resource pressure ──────────────────────────────────────────────────────
  if (fleet.isSuccess) {
    // Disk first: it silently stops package installs, backups and database writes.
    const fullDisks = fleet.data.servers.filter((s) => s.disk_percent !== null && s.disk_percent >= 85);
    const hotMemory = fleet.data.servers.filter((s) => s.memory_percent !== null && s.memory_percent >= 90);

    if (fullDisks.length > 0) {
      const worst = Math.max(...fullDisks.map((s) => s.disk_percent ?? 0));
      attention.push({
        id: 'disk-pressure',
        severity: worst >= 95 ? 'critical' : 'warning',
        message:
          fullDisks.length === 1
            ? `${fullDisks[0].name} is ${worst.toFixed(0)}% full. Installs and backups fail when the disk runs out; the Clean Disk Space playbook can help.`
            : `${fullDisks.length} servers are over 85% disk, the worst at ${worst.toFixed(0)}%.`,
        to: fullDisks.length === 1 ? '/automation/playbooks/clean-disk-space' : '/servers',
        action: fullDisks.length === 1 ? 'Clean disk space' : 'Open servers',
      });
    }

    if (hotMemory.length > 0) {
      attention.push({
        id: 'memory-pressure',
        severity: 'warning',
        message:
          hotMemory.length === 1
            ? `${hotMemory[0].name} is using ${(hotMemory[0].memory_percent ?? 0).toFixed(0)}% of its memory.`
            : `${hotMemory.length} servers are above 90% memory.`,
        to: `/servers/${hotMemory[0].id}`,
        action: 'Open server',
      });
    }
  }

  // ── Checks that could not run ──────────────────────────────────────────────
  // A request that failed has found nothing, which is not the same as nothing being wrong. Without
  // these, one failed load and a quiet everything-else produced the all-clear: "everything being
  // monitored is responding", said about monitors this page never managed to read.
  const unread = [
    { failed: monitors.isError, what: 'Monitors', to: '/monitoring', action: 'Open monitoring' },
    { failed: backups.isError, what: 'Backups', to: '/backups', action: 'Open backups' },
    { failed: waitingRuns.isError || recentRuns.isError, what: 'Runs', to: '/automation/runs', action: 'Open runs' },
    { failed: inventory.isError, what: 'Servers', to: '/servers', action: 'Open servers' },
  ].filter((check) => check.failed);

  for (const check of unread) {
    attention.push({
      id: `unread-${check.to}`,
      severity: 'warning',
      message: `${check.what} could not be loaded, so this page cannot say whether anything there needs attention.`,
      to: check.to,
      action: check.action,
    });
  }

  // Every check must have run before the all-clear can be shown.
  const ready = monitors.isFetched && backups.isFetched && waitingRuns.isFetched && recentRuns.isFetched && inventory.isFetched;

  const sources = backups.data?.sources ?? [];
  const protectedSources = backups.isSuccess
    ? sources.filter((source) => backups.data.schedules.some((schedule) => schedule.source_id === source.id && schedule.enabled)).length
    : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">What needs your attention, and what is running across your servers.</p>
        </div>
        <div className="ml-auto flex flex-wrap justify-end gap-2">
          <QuickAction to="/automation/playbooks" icon={BookOpen} label="Run a playbook" />
          <QuickAction to="/automation/templates" icon={LayoutTemplate} label="Prepare a server" />
          <QuickAction to="/automation/commands" icon={SquareTerminal} label="Run a command" />
        </div>
      </div>

      {/* Before the findings, because both change what the rest of the page means: an account that
          cannot change anything, or a trial with two days left, reads every figure below
          differently. Each renders nothing when it has nothing to say. */}
      <SubscriptionNotice />
      <TrialPanel
        servers={inventory.isSuccess ? { used: inventory.data.quota.in_use, limit: inventory.data.quota.unlimited ? -1 : inventory.data.quota.limit } : undefined}
        monitors={monitors.isSuccess ? { used: monitors.data.quota.used, limit: monitors.data.quota.limit } : undefined}
      />

      <AttentionPanel
        items={attention}
        ready={ready}
        watching={{
          monitors: (monitors.data?.monitors.length ?? 0) > 0,
          backups: (backups.data?.schedules.length ?? 0) > 0,
        }}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <CountTile
          label="Servers"
          tone="servers"
          icon={Server}
          to="/servers"
          value={inventory.isSuccess ? inventory.data.counts.total : undefined}
          // No slash without a real ceiling: unlimited, or 0 for an account whose plan has ended,
          // which would otherwise read "3 / 0".
          limit={inventory.isSuccess && !inventory.data.quota.unlimited && inventory.data.quota.limit > 0 ? inventory.data.quota.limit : null}
          detail={inventory.isSuccess ? `${inventory.data.counts.ready} ready` : undefined}
        />
        <CountTile
          label="Runs, last 24 hours"
          tone="runs"
          icon={History}
          to="/automation/runs"
          // Read from the newest page of runs, so a busy day shows as "25+" rather than a number
          // that silently stopped counting.
          value={
            recentRuns.isSuccess
              ? runsToday.length === recentRuns.data.items.length && recentRuns.data.nextCursor !== null
                ? `${runsToday.length}+`
                : runsToday.length
              : undefined
          }
          // A cancelled run is not a failure, so "none failed" is decided by failures alone: the
          // old test ("every run succeeded") turned one cancellation into "0 did not succeed".
          detail={
            recentRuns.isSuccess
              ? runsToday.length === 0
                ? 'Nothing ran in the last 24 hours'
                : failedToday.length > 0
                  ? `${failedToday.length} did not succeed everywhere`
                  : unfinishedToday.length > 0
                    ? `${unfinishedToday.length} still in progress`
                    : 'None failed'
              : undefined
          }
        />
        <CountTile
          label="Monitors"
          tone="monitors"
          icon={Radar}
          to="/monitoring"
          value={monitors.isSuccess ? monitors.data.quota.used : undefined}
          // A limit of -1 is the API's "no ceiling", and 0 an account with no plan: neither is a
          // number to print after the slash.
          limit={monitors.isSuccess && monitors.data.quota.limit > 0 ? monitors.data.quota.limit : null}
          detail={monitors.isSuccess ? monitorsDetail(monitors.data.monitors) : undefined}
        />
        <CountTile
          label="Backup sources"
          tone="backups"
          icon={Archive}
          to="/backups"
          value={backups.isSuccess ? sources.length : undefined}
          detail={protectedSources !== undefined ? `${protectedSources} on a schedule` : undefined}
        />
      </div>

      <ServerFleet
        servers={fleet.data?.servers ?? []}
        reporting={fleet.data?.reporting ?? 0}
        loading={fleet.isLoading}
        error={fleet.isError ? getErrorMessage(fleet.error) : null}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <UptimeCard
          monitors={monitors.data?.monitors}
          error={monitors.isError ? getErrorMessage(monitors.error) : null}
          fixedThisWeek={
            (incidents.data?.incidents ?? []).filter(
              (incident) =>
                incident.remediation?.status === 'fixed' && new Date(incident.started_at).getTime() >= Date.now() - 7 * 86400 * 1000,
            ).length
          }
        />
        <LatestBackupsCard
          backups={backups.data?.backups}
          error={backups.isError ? getErrorMessage(backups.error) : null}
          verified={backups.data?.verification.summary}
        />
      </div>
    </div>
  );
}

/**
 * Uptime over the last day: the monitoring summary, then the monitors most in need of a look.
 *
 * The four figures are the ones the Monitoring page opens with, worked out by the same function, so
 * the dashboard can never call a monitor up that the page it links to calls not checked recently.
 * They show before the first monitor exists as well — at zero — so what is watched for is visible
 * from the start rather than appearing only once something is.
 */
function UptimeCard({
  monitors,
  error,
  fixedThisWeek,
}: {
  /** Undefined until the list has loaded. */
  monitors: Monitor[] | undefined;
  error: string | null;
  fixedThisWeek: number;
}) {
  const summary = monitors === undefined ? null : summarizeMonitors(monitors);
  const shown = [...(monitors ?? [])].sort((a, b) => monitorUrgency(a) - monitorUrgency(b)).slice(0, LIST_ROWS);
  const hidden = (monitors?.length ?? 0) - shown.length;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Radar className="size-4" />
            Uptime, last 24 hours
          </span>
          <Link to="/monitoring" className="text-xs font-medium text-primary hover:underline">
            All monitors
          </Link>
        </CardTitle>
        <CardDescription>
          Checked from outside your servers.
          {fixedThisWeek > 0 && (
            <span className="mt-1 flex items-center gap-1">
              <Wrench className="size-3.5" />
              {fixedThisWeek === 1 ? 'One problem was fixed automatically this week' : `${fixedThisWeek} problems were fixed automatically this week`}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {/* Four across only where a cell is wide enough for "Not checked recently": the card is
            the full width below lg, and half of it from there up. */}
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 min-[1800px]:grid-cols-4">
          <Figure label="Up" value={summary?.up} tone={summary !== null && summary.up > 0 ? 'text-success' : undefined} />
          <Figure label="Down" value={summary?.down} tone={summary !== null && summary.down > 0 ? 'text-danger' : undefined} />
          <Figure
            label="Not checked recently"
            value={summary?.stale}
            tone={summary !== null && summary.stale > 0 ? 'text-warning' : undefined}
          />
          <Figure
            label="Average uptime"
            value={summary === null ? undefined : summary.averageUptime === null ? 'No data yet' : formatUptimePercent(summary.averageUptime)}
          />
        </dl>

        {error !== null && <p className="text-sm text-danger">Monitors could not be loaded: {error}</p>}
        {monitors === undefined && error === null && <p className="text-sm text-muted-foreground">Loading monitors…</p>}

        {monitors !== undefined && monitors.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing is being monitored yet.{' '}
            <Link to="/monitoring" className="text-primary hover:underline">
              Add a monitor
            </Link>{' '}
            and the platform will check it from its own network.
          </p>
        )}

        {shown.length > 0 && (
          <ul className="space-y-2">
            {shown.map((monitor) => {
              const state = monitorState(monitor);

              return (
                <ListRow key={monitor.id} dot={state.dot} name={monitor.name} detail={monitor.kind_label}>
                  <span className={state.text}>{state.label}</span>
                </ListRow>
              );
            })}
          </ul>
        )}

        {hidden > 0 && (
          <Link to="/monitoring" className="block text-xs text-muted-foreground hover:text-foreground hover:underline">
            and {hidden} more
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

/** What one monitor's row says, and in which colour. A stale reading is never shown as its status. */
function monitorState(monitor: Monitor): { dot: string; text: string; label: string } {
  if (monitor.status === 'paused') {
    return { dot: 'bg-muted-foreground', text: 'text-muted-foreground', label: 'Paused' };
  }

  if (monitor.is_stale) {
    return { dot: 'bg-amber-500', text: 'text-warning', label: 'Not checked recently' };
  }

  if (monitor.status === 'pending') {
    return { dot: 'bg-muted-foreground', text: 'text-muted-foreground', label: 'Waiting for its first check' };
  }

  const down = monitor.status === 'down';
  const dot = down ? 'bg-danger' : 'bg-emerald-500';
  const text = down ? 'text-danger' : 'text-muted-foreground';

  // An expiry monitor is asked how long is left, and a percentage does not answer that.
  if (isExpiryKind(monitor.kind)) {
    return { dot, text, label: expiryLabel(monitor.certificate_days_remaining) };
  }

  const uptime =
    monitor.uptime_24h === null || monitor.uptime_24h === undefined ? null : formatUptimePercent(monitor.uptime_24h);

  if (down) {
    return { dot, text, label: uptime === null ? 'Down' : `Down · ${uptime}` };
  }

  return { dot, text, label: uptime ?? 'Not enough checks yet' };
}

/** The newest backups, failed ones included: a failure hidden here is found on the day it matters. */
function LatestBackupsCard({
  backups,
  error,
  verified,
}: {
  /** Undefined until the overview has loaded. */
  backups: BackupRecord[] | undefined;
  error: string | null;
  verified: { sources_verified: number; sources_backed_up: number } | undefined;
}) {
  const shown = (backups ?? []).slice(0, LIST_ROWS);
  const hidden = (backups?.length ?? 0) - shown.length;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <Archive className="size-4" />
            Latest backups
          </span>
          <Link to="/backups" className="text-xs font-medium text-primary hover:underline">
            All backups
          </Link>
        </CardTitle>
        <CardDescription>
          Newest first, failed ones included.
          {verified !== undefined && verified.sources_backed_up > 0 && (
            <span className="mt-1 flex items-center gap-1">
              <ShieldCheck className="size-3.5" />
              {verified.sources_verified} of {verified.sources_backed_up} source
              {verified.sources_backed_up === 1 ? '' : 's'} restore-tested this week
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {error !== null && <p className="text-sm text-danger">Backups could not be loaded: {error}</p>}
        {backups === undefined && error === null && <p className="text-sm text-muted-foreground">Loading backups…</p>}

        {backups !== undefined && backups.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No backups yet.{' '}
            <Link to="/backups" className="text-primary hover:underline">
              Add a source and a destination
            </Link>{' '}
            to start copying what matters on your servers.
          </p>
        )}

        {shown.length > 0 && (
          <ul className="space-y-2">
            {shown.map((backup) => (
              <ListRow
                key={backup.id}
                dot={backup.status === 'succeeded' ? 'bg-emerald-500' : backup.status === 'failed' ? 'bg-danger' : 'bg-amber-500'}
                name={backup.source_name}
                detail={`to ${backup.destination_name}`}
              >
                {backup.status === 'failed' && <span className="text-danger">Failed</span>}
                {backup.status === 'running' && <span className="text-warning">Running</span>}
                {backup.trust === 'restorable' && (
                  <span title="Restore tested">
                    <ShieldCheck aria-label="Restore tested" className="size-3.5 text-emerald-600" />
                  </span>
                )}
                {backup.trust === 'failed' && <Badge variant="danger">failed check</Badge>}
                <span title={new Date(backup.started_at).toLocaleString()}>{relativeTime(backup.started_at)}</span>
              </ListRow>
            ))}
          </ul>
        )}

        {hidden > 0 && (
          <Link to="/backups" className="block text-xs text-muted-foreground hover:text-foreground hover:underline">
            and {hidden} more
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

/** One row of the lists at the foot of the page: a status dot, a name with a quiet detail, and the state. */
function ListRow({ dot, name, detail, children }: { dot: string; name: string; detail: string; children: ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3 text-sm">
      <span className="flex min-w-0 items-center gap-2">
        <span aria-hidden className={`size-2 shrink-0 rounded-full ${dot}`} />
        <span className="truncate">{name}</span>
        <span className="hidden truncate text-xs text-muted-foreground sm:inline">{detail}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2 tabular-nums text-muted-foreground">{children}</span>
    </li>
  );
}

/** A small labelled figure. Undefined while its query is loading: drawn as a pulse, never as zero. */
function Figure({ label, value, tone }: { label: string; value: number | string | undefined; tone?: string | undefined }) {
  return (
    <div className="min-w-0 rounded-md bg-muted/30 px-3 py-2">
      <dt className="truncate text-xs text-muted-foreground" title={label}>
        {label}
      </dt>
      <dd className={`text-lg font-semibold tabular-nums ${tone ?? ''}`}>{value ?? <PendingValue />}</dd>
    </div>
  );
}

/**
 * The colours the tiles are keyed by, so the tile you want is findable without reading. Held as whole
 * class strings because Tailwind scans the source for literals.
 */
const TILE_TONES = {
  servers: 'bg-sky-500/10 text-sky-500',
  runs: 'bg-violet-500/10 text-violet-500',
  monitors: 'bg-emerald-500/10 text-emerald-500',
  backups: 'bg-amber-500/10 text-amber-500',
} as const;

/**
 * How the monitors stand, in a few words. Down first, then not checked recently — never folded into
 * each other, and never "none down" said about monitors whose readings are out of date.
 */
function monitorsDetail(monitors: Monitor[]): string {
  if (monitors.length === 0) return 'None yet';

  const down = monitors.filter((monitor) => monitor.status === 'down' && !monitor.is_stale).length;
  const stale = monitors.filter((monitor) => monitor.is_stale).length;
  const up = monitors.filter((monitor) => monitor.status === 'up' && !monitor.is_stale).length;

  if (down > 0) return `${down} down`;
  if (stale > 0) return `${stale} not checked recently`;

  return `${up} up`;
}

/**
 * A count, and for the two things a plan limits, the limit beside it: "3 / 5".
 *
 * The owner's format, restored after "plan allows 5" replaced it: the allowance reads as part of the
 * figure rather than as a sentence underneath, and a tile at its limit says so in amber — the only
 * point at which the limit needs saying at all.
 */
function CountTile({
  label,
  icon: Icon,
  to,
  value,
  limit = null,
  detail,
  tone,
}: {
  label: string;
  icon: typeof Server;
  to: string;
  /** Undefined while not known — rendered as a dash, never as zero. */
  value: number | string | undefined;
  /** The plan's ceiling for this count; null when there is none, or it is not a limited count. */
  limit?: number | null;
  detail: string | undefined;
  tone: keyof typeof TILE_TONES;
}) {
  const atLimit = typeof value === 'number' && limit !== null && value >= limit;

  return (
    <Link to={to} className="block">
      <Card className={`h-full p-5 transition-colors hover:bg-muted/40 ${atLimit ? 'border-amber-600/40' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm text-muted-foreground">{label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
              {value ?? <PendingValue />}
              {value !== undefined && limit !== null && (
                <span className="text-base font-normal text-muted-foreground"> / {limit}</span>
              )}
            </p>
            <p className={`mt-1 truncate text-xs ${atLimit ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {atLimit ? 'Plan limit reached' : (detail ?? ' ')}
            </p>
          </div>
          <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${TILE_TONES[tone]}`}>
            <Icon className="size-[18px]" />
          </span>
        </div>
      </Card>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label }: { to: string; icon: typeof Server; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-muted/50"
    >
      <Icon className="size-4" aria-hidden />
      {label}
    </Link>
  );
}
